import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import { btn, errMsg } from './ui';

// Read a File → downscaled data URL (max edge 1600px). PNG is kept for transparency (artwork), else JPEG.
export const fileToDataUrl = (file, max = 1600) => new Promise((resolve, reject) => {
  if (!file.type.startsWith('image/')) { reject(new Error('Please choose an image file')); return; }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    const png = file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/svg+xml';
    resolve(png ? c.toDataURL('image/png') : c.toDataURL('image/jpeg', 0.88));
    URL.revokeObjectURL(url);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image')); };
  img.src = url;
});

// Upload a data URL to the store's media library → public URL (/api/public/media/{id}).
export const uploadMedia = async (storeId, dataUrl, kind = 'product', filename = '') => {
  const { data } = await axios.post(`/stores/${storeId}/media`, { image_base64: dataUrl, kind, filename });
  return data.url;
};

export const UploadButton = ({ storeId, kind = 'product', children = 'Upload image', onUploaded, testId, style, capture }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('Image is larger than 20MB'); return; }
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file, kind === 'artwork' ? 2000 : 1600);
      const url = await uploadMedia(storeId, dataUrl, kind, file.name);
      onUploaded(url, { dataUrl, filename: file.name });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response ? errMsg(err, 'Upload failed') : (err.message || 'Upload failed'));
    } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture={capture} onChange={pick} style={{ display: 'none' }} data-testid={testId ? `${testId}-input` : undefined} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testId} style={{ ...btn, ...style, opacity: busy ? 0.6 : 1 }}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {busy ? 'Uploading…' : children}
      </button>
    </>
  );
};
